import React, { ReactNode, useEffect, useState } from 'react';
import { EfficacyStats } from 'components/global/EfficacyStats';
import { CtaButton, type CtaButtonProps } from 'components/global/CtaButton';
import { ReactComponent as SqueezeSVG } from './images/mobileSqueeze.svg';
import type { Theme } from '../types';

import {
  HeroPopEfficacyStatsContainer,
  HeroPopContainer,
  HeroPopLeftContent,
  HeroPopSqueeze,
  HeroPopSubtitle,
  HeroPopTitle,
  heroPopCtaStyles,
  HeroPopImageContainer,
  HeroPopDisclaimer,
  HeroPopValueProps,
  HeroPopValueProp,
  HeroPopBadgeContainer,
} from './HeroPop.style';

import { LOCAL_COLORS } from 'styles/constants';

interface EfficacyStatPop {
  animation: {
    start: number;
    end: number;
  };
  postfix: string;
  label: string;
  superscriptSymbol?: string;
}

export interface HeroPopProps {
  images: ReactNode;
  title: string;
  subtitle?: string;
  efficacyStats?: EfficacyStatPop[];
  ctaButton?: CtaButtonProps;
  updateInterval?: number;
  maxUpdates?: number;
  heroClass?: string;
  theme?: Theme;
  promoPeriod?: string | undefined;
  showStats?: boolean;
  backgroundColor?: string;
  disclaimer?: string;
  addSqueezeMask?: boolean;
  valueProps?: string[];
  showBadge?: boolean;
  badgeImages?: ReactNode;
}

const moduleName = 'heroPop';

export const HeroPop = ({
  images,
  title,
  subtitle,
  efficacyStats = [],
  ctaButton,
  updateInterval = 200,
  heroClass,
  theme = 'primary',
  showStats,
  backgroundColor = LOCAL_COLORS.surfaceBrandBolder,
  disclaimer,
  addSqueezeMask = true,
  valueProps,
  showBadge = false,
  badgeImages,
}: HeroPopProps) => {
  const [efficacyProgress, setEfficacyProgress] = useState(
    efficacyStats.map(({ animation }) => {
      return [animation.start, animation.end];
    }),
  );

  const formattedEfficacyProps = efficacyStats.map((stat, index) => {
    return {
      label: stat.label,
      superscriptSymbol: stat.superscriptSymbol,
      amount: `${efficacyProgress[index][0]}${stat.postfix}`,
    };
  });

  const runEfficacyUpdate = () => {
    const handler = window.setTimeout(() => {
      const remaining = efficacyProgress.filter(
        ([current, end]) => current !== end,
      );
      if (remaining.length === 0) {
        return;
      }

      setEfficacyProgress((previousStats) => {
        const updatedStats = [];

        for (const stat of previousStats) {
          const [current, end] = stat;

          if (current === end) {
            updatedStats.push(stat);
            continue;
          }

          updatedStats.push([current + 1, end]);
        }

        return updatedStats;
      });
    }, updateInterval);

    return () => window.clearTimeout(handler);
  };

  // Ticks up the efficacy stat numbers on a set timer
  useEffect(runEfficacyUpdate, []);
  useEffect(runEfficacyUpdate, [efficacyProgress]);

  return (
    <HeroPopContainer
      data-module={moduleName}
      backgroundColor={backgroundColor}
      className={heroClass}
    >
      {showBadge && (
        <HeroPopBadgeContainer>{badgeImages}</HeroPopBadgeContainer>
      )}
      <HeroPopImageContainer className={heroClass}>
        {images}
        <HeroPopLeftContent className={heroClass} themeType={theme}>
          <HeroPopTitle
            className={heroClass}
            themeType={theme}
            dangerouslySetInnerHTML={{
              __html: title,
            }}
          />
          {subtitle && (
            <HeroPopSubtitle
              className={heroClass}
              dangerouslySetInnerHTML={{
                __html: subtitle,
              }}
            />
          )}
          {valueProps && (
            <HeroPopValueProps>
              {valueProps.map((prop) => (
                <HeroPopValueProp
                  key={prop}
                  dangerouslySetInnerHTML={{ __html: prop }}
                />
              ))}
            </HeroPopValueProps>
          )}
          {efficacyStats.length > 0 && showStats && (
            <HeroPopEfficacyStatsContainer themeType={theme}>
              <EfficacyStats stats={formattedEfficacyProps} />
            </HeroPopEfficacyStatsContainer>
          )}
          {ctaButton && (
            <CtaButton
              {...ctaButton}
              module={moduleName}
              className={heroClass}
              css={heroPopCtaStyles}
              isExternal={true}
            />
          )}
          {disclaimer && <HeroPopDisclaimer>{disclaimer}</HeroPopDisclaimer>}
        </HeroPopLeftContent>
        {addSqueezeMask && (
          <HeroPopSqueeze
            backgroundColor={backgroundColor}
            className={heroClass}
          >
            <SqueezeSVG />
          </HeroPopSqueeze>
        )}
      </HeroPopImageContainer>
    </HeroPopContainer>
  );
};
